/// <reference types="./proposal_builder.d.mts" />
import * as $proposal_builder from "../../../common/data/ephemeral/proposal_builder.mjs";
import * as $project from "../../../common/data/project.mjs";
import * as $proposal from "../../../common/data/proposal.mjs";
import * as $proposal_element from "../../../common/data/proposal_element.mjs";
import * as $question from "../../../common/data/proposal_element/question.mjs";
import * as $http from "../../../gleam_http/gleam/http.mjs";
import * as $promise from "../../../gleam_javascript/gleam/javascript/promise.mjs";
import * as $decode from "../../../gleam_stdlib/gleam/dynamic/decode.mjs";
import * as $function from "../../../gleam_stdlib/gleam/function.mjs";
import * as $option from "../../../gleam_stdlib/gleam/option.mjs";
import { None, Some } from "../../../gleam_stdlib/gleam/option.mjs";
import * as $result from "../../../gleam_stdlib/gleam/result.mjs";
import * as $effect from "../../../lustre/lustre/effect.mjs";
import * as $state from "../../data/model/state.mjs";
import * as $msg from "../../data/msg.mjs";
import * as $ask from "../../frontend/ask.mjs";
import * as $error from "../../frontend/error.mjs";
import * as $middleware from "../../frontend/middleware.mjs";
import { Ok, Error, toList, makeError } from "../../gleam.mjs";

const FILEPATH = "src/frontend/effects/proposal_builder.gleam";

function create_project(access_token, state, org_id, user_id) {
  let builder = state.proposal_builder;
  let _pipe = $ask.to(new $ask.Heimdall(), toList(["projects"]));
  let _pipe$1 = $ask.bearing(_pipe, access_token);
  let _pipe$2 = $ask.via(_pipe$1, new $http.Post());
  let _pipe$3 = $ask.with$(
    _pipe$2,
    $project.encode($proposal_builder.project(builder, org_id, user_id)),
  );
  let _pipe$4 = $ask.expect(_pipe$3, $project.decoder());
  return $ask.run(_pipe$4);
}

function proposal_elements_decoder() {
  return $decode.field(
    "proposal",
    $proposal.decoder(),
    (proposal) => {
      let elements = $decode.list($proposal_element.decoder());
      return $decode.field(
        "elements",
        elements,
        (elements) => { return $decode.success([proposal, elements]); },
      );
    },
  );
}

function proposal_question_decoder() {
  return $decode.field(
    "proposal",
    $proposal.decoder(),
    (proposal) => {
      let _block;
      let _pipe = $question.decoder();
      _block = $decode.map(
        _pipe,
        (var0) => { return new $proposal_element.Question(var0); },
      );
      let question = _block;
      return $decode.field(
        "questions",
        $decode.list(question),
        (questions) => { return $decode.success([proposal, questions]); },
      );
    },
  );
}

function create_proposal(access_token, state, org_id) {
  let _pipe = $proposal_builder.to_form_data(state.proposal_builder, org_id);
  let _pipe$1 = $result.replace_error(
    _pipe,
    new $error.NilError("Unable to encode"),
  );
  let _pipe$2 = $promise.resolve(_pipe$1);
  return $promise.try_await(
    _pipe$2,
    (form_data) => {
      let _block;
      let $ = state.proposal_builder.proposal_spreadsheet.xlsx;
      let $1 = state.proposal_builder.proposal_document.document;
      let $2 = state.proposal_builder.proposal_pdf.outline;
      if ($ instanceof Some) {
        _block = toList(["proposals", "sheet"]);
      } else if ($1 instanceof Some) {
        _block = toList(["proposals", "doc"]);
      } else if ($2 instanceof Some) {
        _block = toList(["proposals", "narrative"]);
      } else {
        throw makeError(
          "panic",
          FILEPATH,
          "frontend/effects/proposal_builder",
          90,
          "create_proposal",
          "Proposal Builder should be of one mode at least",
          {}
        )
      }
      let at = _block;
      let _pipe$3 = $ask.to(new $ask.Heimdall(), at);
      let _pipe$4 = $ask.bearing(_pipe$3, access_token);
      let _pipe$5 = $ask.via(_pipe$4, new $http.Post());
      let _pipe$6 = $ask.data(_pipe$5, form_data);
      let _pipe$7 = $ask.expect(
        _pipe$6,
        (() => {
          let elements = proposal_elements_decoder();
          let questions = proposal_question_decoder();
          return $decode.one_of(elements, toList([questions]));
        })(),
      );
      return $ask.run(_pipe$7);
    },
  );
}

export function send_proposal_builder(state) {
  return $middleware.require_access_token(
    (access_token) => {
      return $middleware.require_org_id(
        state,
        (org_id) => {
          return $middleware.require_user_id(
            state,
            (user_id) => {
              return $effect.from(
                (dispatch) => {
                  return $function.tap(
                    undefined,
                    (_) => {
                      let status = state.proposal_builder.edit_status;
                      let _block;
                      if (status instanceof $proposal_builder.NewProject) {
                        let project = create_project(
                          access_token,
                          state,
                          org_id,
                          user_id,
                        );
                        _block = $promise.map_try(
                          project,
                          (project) => {
                            dispatch(
                              new $msg.Project(
                                new $msg.ApiReturnedProjects(
                                  toList([project]),
                                  false,
                                ),
                              ),
                            );
                            return new Ok(project.id);
                          },
                        );
                      } else if (status instanceof $proposal_builder.NewProposal) {
                        let $ = state.proposal_builder.project_id;
                        if ($ instanceof Some) {
                          let project_id = $[0];
                          _block = $promise.resolve(new Ok(project_id));
                        } else {
                          _block = $promise.resolve(
                            new Error(new $error.NilError("No project_id")),
                          );
                        }
                      } else if (status instanceof $proposal_builder.EditProject) {
                        _block = $promise.resolve(new Ok(status.project_id));
                      } else {
                        _block = $promise.resolve(new Ok(status.project_id));
                      }
                      let _pipe = _block;
                      let _pipe$1 = $promise.try_await(
                        _pipe,
                        (project_id) => {
                          let _block$1;
                          let _record = state.proposal_builder;
                          _block$1 = new $proposal_builder.ProposalBuilder(
                            new $proposal_builder.NewProposal(),
                            _record.step,
                            _record.project_attachments,
                            _record.project_client,
                            _record.project_collaborators,
                            _record.project_deadline,
                            _record.project_name,
                            _record.project_owners,
                            new Some(project_id),
                            _record.proposal_pre_filled,
                            _record.proposal_name,
                            _record.proposal_tags,
                            _record.proposal_additional_context,
                            _record.proposal_style_guidelines,
                            _record.proposal_default_language,
                            _record.proposal_languages,
                            _record.proposal_qualification_matrix,
                            _record.proposal_spreadsheet,
                            _record.proposal_document,
                            _record.proposal_pdf,
                            _record.proposal_one_trust,
                            _record.proposal_link_id,
                            _record.salesforce_id,
                          );
                          let _pipe$1 = _block$1;
                          let _pipe$2 = ((_capture) => {
                            return $state.set_proposal_builder(state, _capture);
                          })(_pipe$1);
                          return ((_capture) => {
                            return create_proposal(
                              access_token,
                              _capture,
                              org_id,
                            );
                          })(_pipe$2);
                        },
                      );
                      return $promise.map(
                        _pipe$1,
                        (saved_proposal) => {
                          let _pipe$2 = saved_proposal;
                          let _pipe$3 = $result.replace_error(
                            _pipe$2,
                            toList([]),
                          );
                          let _pipe$4 = new $msg.ApiReturnedSavedProposal(
                            _pipe$3,
                          );
                          let _pipe$5 = new $msg.ProposalBuilder(_pipe$4);
                          return dispatch(_pipe$5);
                        },
                      );
                    },
                  );
                },
              );
            },
          );
        },
      );
    },
  );
}
